import { rpx } from 'client/lib/rpx-client';
import { LoginWrapper } from '@course/components/login-wrapper';
import { AutoForm, FormGroup, useAutoFormState } from '@components/async-form';
import { Password } from '@components/password';
import { useAuth, useCurrentTenant } from '@components/router/session-context';
import { useDocumentTitle } from 'client/utils/use-document-title';
import { useAsyncEffect } from 'client/utils/use-async-effect';
import { useEffect } from 'preact/hooks';
import { showToast } from '@components/toaster';
import { useIntl } from 'shared/intl/use-intl';
import { V1_USER } from 'shared/consts';
import { getRedirectUrl } from 'client/utils/url';
import { LoadedProps, RouteLoadProps, defRoute } from '@components/router';
import { StyledBrandButton } from '@course/components/styled-brand-button';

export const route = defRoute({
  load,
  isPublic: true,
  Page,
});

async function load(route: RouteLoadProps) {
  const { courseId, brandId } = route.params;
  if (!courseId && !brandId) {
    return { brand: undefined };
  }

  const brand = await rpx.branding.getBrand({ courseId, brandId });
  return { brand };
}

type Props = LoadedProps<typeof load>;

function submitV1Login(data: any) {
  const doc = document;
  // Don't send the referrer info to v1, so it assumes we're app.ruzuku.com.
  doc.head.insertAdjacentHTML('beforeend', '<meta name="referrer" content="never" />');
  // Inject the v1 login form.
  doc.body.insertAdjacentHTML(
    'beforeend',
    `
    <form style="display:none" id="v1loginform" method="POST" action="https://app.ruzuku.com/session">
      <input name="login" />
      <input name="password" />
      <button>Submit</button>
    </form>
  `,
  );

  // Fill out and submit the v1 login form.
  doc.querySelector<HTMLInputElement>('#v1loginform input[name=login]')!.value = data.email;
  doc.querySelector<HTMLInputElement>('#v1loginform input[name=password]')!.value = data.password;
  doc.querySelector<HTMLFormElement>('#v1loginform')!.submit();
}

function Page(props: Props) {
  const intl = useIntl();
  const auth = useAuth();
  const tenant = useCurrentTenant();
  const emailParam = props.params.email;
  const [state, ctx] = useAutoFormState({
    initialState: {
      email: emailParam || '',
      password: '',
    },
  });

  const brand = props.state.brand || tenant;

  useDocumentTitle([
    intl('Sign in to {tenant:string}', {
      tenant: tenant.name,
    }),
  ]);

  async function login(data: any) {
    try {
      const loggedInUser = await auth.login(rpx.auth.login(data));
      props.router.goto(getRedirectUrl(loggedInUser, tenant));
      // Keep the form spinning while we redirect the SPA.
      await new Promise((r) => setTimeout(r, 1000));
    } catch (err) {
      if (err?.data === V1_USER) {
        submitV1Login(data);
        // Keep the form spinning while we redirect to v1.
        return new Promise(() => {});
      }
      throw err;
    }
  }

  useEffect(() => {
    if (emailParam) {
      showToast({
        type: 'warn',
        title: intl('Email address already exists'),
        message: intl(
          `You will get access to the course creation tools as soon as you log in. You may use "Forgot your password?" button if you don't remember your password.`,
        ),
        timeout: 60000,
      });
    }
  }, [emailParam]);

  /*
   * Users may sometimes be redirected to the login page even if
   * they have an active session. So we're checking that here
   * and redirecting them to `redirect` URL or home page if the session exists.
   */
  useAsyncEffect(async () => {
    if (auth.user && !props.params.preview) {
      props.router.goto(getRedirectUrl(auth.user, tenant));
    }
  }, []);

  return (
    <LoginWrapper brand={brand}>
      <AutoForm onSubmit={login} ctx={ctx}>
        <div class="flex flex-col gap-6 mt-2">
          <FormGroup prop="email" class="w-full">
            <label class="block text-sm font-medium text-gray-700 mb-1">{intl('Email')}</label>
            <input
              type="text"
              placeholder={intl('you@example.com')}
              name="email"
              defaultValue={emailParam}
              value={state.email}
              class="ruz-input block w-full sm:text-sm sm:leading-5 bg-transparent py-3"
              data-private
            />
          </FormGroup>
          <FormGroup prop="password" class="w-full">
            <label class="block text-sm font-medium text-gray-700 mb-1">{intl('Password')}</label>
            <Password name="password" placeholder="••••••••" class="bg-transparent py-3" />
          </FormGroup>
          <StyledBrandButton brand={brand} isLoading={ctx.isProcessing}>
            {intl('Sign in')}
          </StyledBrandButton>
        </div>
      </AutoForm>

      <footer class="text-center block mt-4">
        <a
          href={`/forgot-password?email=${encodeURIComponent(state.email)}`}
          class="focus-within:outline-current outline-2 outline-transparent"
          style={{
            color: brand.primaryActionColor,
          }}
        >
          {intl('Forgot your password?')}
        </a>
      </footer>
    </LoginWrapper>
  );
}
